<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- Teleport the toast to body to avoid layout issues in nested containers -->
  <Teleport to="body">
    <div
      v-if="isVisible"
      class="toast-module"
      :class="[`toast-module--${type}`, { 'toast-module--exit': isExiting }]"
    >
      <div class="toast-module__content">
        <!-- Icon slot allows custom icons, defaults to type-based Lucide icons -->
        <slot name="icon">
          <component :is="defaultIconComponent" class="toast-module__icon" />
        </slot>
        <div class="toast-module__text">
          <!-- Main message slot for custom content -->
          <span class="toast-module__message">
            <slot>{{ message }}</slot>
          </span>
          <!-- Optional subtitle, shown only if enabled -->
          <span v-if="showSubtitle && subtitle" class="toast-module__subtitle">
            {{ subtitle }}
          </span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { CheckCircle, AlertCircle, Info, Loader2 } from "lucide-vue-next";

// Define props for toast configuration
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: "",
  },
  subtitle: {
    type: String,
    default: "",
  },
  showSubtitle: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "info",
    validator: (value) =>
      ["info", "saved", "error", "warning", "working"].includes(value),
  },
  autoHide: {
    type: Boolean,
    default: true,
  },
  duration: {
    type: Number,
    default: 3000,
  },
});

// Reactive state for visibility and exit animation
const isVisible = ref(false);
const isExiting = ref(false);

// Watch show prop to handle enter/exit animations
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      // Show toast with enter animation
      isExiting.value = false;
      isVisible.value = true;

      // Auto-hide after duration if enabled
      if (props.autoHide) {
        setTimeout(() => {
          startExit();
        }, props.duration);
      }
    } else {
      startExit();
    }
  },
  { immediate: true }
);

// Function to start exit animation
function startExit() {
  if (!isVisible.value) return;

  // Start exit animation
  isExiting.value = true;
  // Hide toast after animation completes
  setTimeout(() => {
    isVisible.value = false;
    isExiting.value = false;
  }, 300); // Match animation duration
}

// Computed property to select default icon based on type
const defaultIconComponent = computed(() => {
  const icons = {
    saved: CheckCircle,
    error: AlertCircle,
    warning: AlertCircle,
    info: Info,
    working: Loader2,
  };
  return icons[props.type] || Info;
});
</script>

<style scoped>
.toast-module {
  position: fixed;
  left: 50%;
  bottom: calc(6rem + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 100000;
  width: max-content;
  max-width: 90vw;
  min-width: 8rem;
  animation: toast-slide-in 0.3s ease-out;
}

.toast-module--exit {
  animation: toast-slide-out 0.3s ease-in forwards;
}

.toast-module__content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-strong);
  border: 1px solid;
  font-weight: 500;
  font-size: 0.875rem;
  background-color: var(--color-primary-100);
  color: var(--color-text);
}

.toast-module__text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.toast-module__icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.toast-module__message {
  line-height: 1.4;
}

.toast-module__subtitle {
  font-size: 0.75rem;
  opacity: 0.7;
  line-height: 1.3;
}

/* Toast Variants */
.toast-module--saved .toast-module__content {
  border-color: var(--color-success);
}

.toast-module--saved .toast-module__icon {
  color: var(--color-success);
}

.toast-module--error .toast-module__content {
  border-color: var(--color-error);
}

.toast-module--error .toast-module__icon {
  color: var(--color-error);
}

.toast-module--warning .toast-module__content {
  border-color: #f59e0b;
}

.toast-module--warning .toast-module__icon {
  color: #f59e0b;
}

.toast-module--info .toast-module__content {
  border-color: var(--color-primary-400);
}

.toast-module--info .toast-module__icon {
  color: var(--color-primary-600);
}

.toast-module--working .toast-module__content {
  border-color: var(--color-primary-400);
  gap: 0.75rem;
  padding: 1rem 1.5rem;
}

.toast-module--working .toast-module__icon {
  animation: spin 1s linear infinite;
}

.toast-module--working .toast-module__message {
  white-space: nowrap;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(6rem);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes toast-slide-out {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  to {
    opacity: 0;
    transform: translateX(-50%) translateY(6rem);
  }
}
</style>
